import { useState, useEffect, useCallback } from 'react'
import Modal from 'react-modal'
import axios from 'axios'
import { Navigation } from 'swiper'

import api from 'services/api'
import { trackEvent } from 'services/analytics'

import { ModelCity } from 'models/ModelCity'
import { ModelItineraries } from 'models/ModelItineraries'
import { getItineraryPublicPath } from 'utils/itineraryUrl'

import { CurrencyInfo, getCurrencyInfo } from './getCurrency'
import GoogleMapsImage from './googleMapsImage'
import { getButtonAffiliateLinks } from './links'
import * as S from './style'
import { getViatorAffiliateUrl } from './viator'

type CityModalProps = {
  isOpen: boolean
  closeModal: () => void
  city: ModelCity
  shouldCloseOnOverlayClick?: boolean
}

type VisitorContext = {
  currency: string
  timezone: string
  country: string
}

type WikipediaPage = {
  extract?: string
  original?: { source?: string }
  thumbnail?: { source?: string }
}

type WikipediaGeoResult = {
  pageid?: number
  title?: string
  dist?: number
}

type PartnerAd = {
  id: string
  campaignName: string
  partnerName: string
  destinationUrl: string
  imageUrl: string
  mobileImageUrl?: string | null
  format: 'horizontal' | 'card'
  priority: number
}

type DestinationCoverCredit = {
  provider?: string | null
  sourceUrl?: string | null
  photographer?: string | null
  photographerUrl?: string | null
}

const weatherLabel = (code?: number) => {
  if (code === undefined || code === null) return 'Condição atual'
  if (code === 0) return 'Céu limpo'
  if ([1, 2].includes(code)) return 'Parcialmente nublado'
  if (code === 3) return 'Nublado'
  if ([45, 48].includes(code)) return 'Neblina'
  if ([51, 53, 55, 56, 57].includes(code)) return 'Garoa'
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return 'Chuva'
  if ([71, 73, 75, 77, 85, 86].includes(code)) return 'Neve'
  if ([95, 96, 99].includes(code)) return 'Trovoadas'
  return 'Condição atual'
}

const formatTime = (date: Date, timezone?: string) => {
  if (!timezone) return '--:--'

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  } catch {
    return '--:--'
  }
}

const currencySymbol = (currency: string) => {
  try {
    return (
      new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      })
        .formatToParts(1)
        .find((part) => part.type === 'currency')?.value || currency
    )
  } catch {
    return currency
  }
}

const normalizeLabel = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const formatExchangeRate = (
  currencyInfo: CurrencyInfo,
  visitorSymbol: string,
  destinationSymbol: string
) => {
  const rate = Number(currencyInfo.exchangeRate)

  if (!currencyInfo.rateAvailable || !Number.isFinite(rate) || rate <= 0) {
    return 'Cotação indisponível'
  }

  if (rate >= 1) {
    return `${visitorSymbol} 1 = ${destinationSymbol} ${rate.toLocaleString(
      'pt-BR',
      { maximumFractionDigits: 3 }
    )}`
  }

  const inverseRate = 1 / rate
  return `${destinationSymbol} 1 = ${visitorSymbol} ${inverseRate.toLocaleString(
    'pt-BR',
    { maximumFractionDigits: 3 }
  )}`
}

const trimDescription = (extract: string) => {
  const paragraphs = extract
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .slice(0, 3)

  const description = paragraphs.join('\n\n')
  if (description.length <= 1600) return description

  const cut = description.slice(0, 1597)
  const lastSentence = cut.lastIndexOf('.')
  return `${lastSentence > 900 ? cut.slice(0, lastSentence + 1) : cut}...`
}

const CityModal = ({
  isOpen,
  closeModal,
  city,
  shouldCloseOnOverlayClick = true
}: CityModalProps) => {
  const { id, name, latitude, longitude } = city

  const [cityDetails, setCityDetails] = useState<ModelCity>(city)
  const [cityDescription, setCityDescription] = useState('')
  const [heroImage, setHeroImage] = useState<string | undefined>()
  const [heroCredit, setHeroCredit] = useState<DestinationCoverCredit | null>(
    null
  )
  const [experienceImage, setExperienceImage] = useState<string | undefined>()
  const [temperature, setTemperature] = useState<number | null>(null)
  const [apparentTemperature, setApparentTemperature] = useState<number | null>(
    null
  )
  const [weatherCode, setWeatherCode] = useState<number | undefined>()
  const [destinationTimezone, setDestinationTimezone] = useState<string>('')
  const [now, setNow] = useState(new Date())
  const [visitorContext, setVisitorContext] = useState<VisitorContext>({
    currency: 'BRL',
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
    country: ''
  })
  const [currencyInfo, setCurrencyInfo] = useState<CurrencyInfo | null>(null)
  const [itineraries, setItineraries] =
    useState<Array<ModelItineraries> | null>()
  const [partnerAds, setPartnerAds] = useState<PartnerAd[]>([])

  const getCityDetails = useCallback(async () => {
    try {
      const { data } = await api.get(`/cities/${id}`)
      const detail = Array.isArray(data) ? data[0] : data

      if (detail) setCityDetails(detail)
    } catch {
      setCityDetails(city)
    }
  }, [city, id])

  const getClimate = useCallback(async () => {
    try {
      const response = await axios.get(
        'https://api.open-meteo.com/v1/forecast',
        {
          params: {
            latitude,
            longitude,
            current: 'temperature_2m,apparent_temperature,weather_code',
            timezone: 'auto'
          }
        }
      )

      setTemperature(Math.round(response.data?.current?.temperature_2m))
      setApparentTemperature(
        Math.round(response.data?.current?.apparent_temperature)
      )
      setWeatherCode(response.data?.current?.weather_code)
      setDestinationTimezone(response.data?.timezone || '')
    } catch {
      setTemperature(null)
      setApparentTemperature(null)
      setDestinationTimezone('')
    }
  }, [latitude, longitude])

  const getVisitorContext = useCallback(() => {
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo'

    setVisitorContext({
      currency: 'BRL',
      timezone,
      country: ''
    })
  }, [])

  const getItinerariesByCoordinate = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/itineraries/coordinates?latitude=${latitude}&longitude=${longitude}`
      )
      setItineraries(data)
    } catch {
      setItineraries([])
    }
  }, [latitude, longitude])

  const getDestinationEditorial = useCallback(async () => {
    const cityName = name.split(',')[0].trim()
    const parsedLatitude = Number(latitude)
    const parsedLongitude = Number(longitude)
    let hasManagedCover = false

    try {
      const { data: cover } = await api.get(`/cities/${id}/cover-meta`)
      if (cover?.imageUrl) {
        hasManagedCover = true
        setHeroImage(cover.imageUrl)
        setHeroCredit({
          provider: cover.provider,
          sourceUrl: cover.sourceUrl,
          photographer: cover.photographer,
          photographerUrl: cover.photographerUrl
        })
      }
    } catch {
      // Enquanto a capa curada não existe, mantemos os fallbacks editoriais.
    }

    const fetchWikipediaPage = async (language: 'pt' | 'en') => {
      let pageId: number | undefined

      if (Number.isFinite(parsedLatitude) && Number.isFinite(parsedLongitude)) {
        try {
          const nearbyResult = await axios.get(
            `https://${language}.wikipedia.org/w/api.php`,
            {
              params: {
                action: 'query',
                list: 'geosearch',
                gscoord: `${parsedLatitude}|${parsedLongitude}`,
                gsradius: 10000,
                gslimit: 15,
                format: 'json',
                origin: '*'
              }
            }
          )

          const nearbyPages = (nearbyResult.data?.query?.geosearch ||
            []) as WikipediaGeoResult[]
          const normalizedCityName = normalizeLabel(cityName)
          const selected =
            nearbyPages.find(
              (item) => normalizeLabel(item.title || '') === normalizedCityName
            ) ||
            nearbyPages.find((item) =>
              normalizeLabel(item.title || '').startsWith(normalizedCityName)
            ) ||
            nearbyPages[0]

          pageId = selected?.pageid
        } catch {
          // Se a busca geográfica falhar, tentamos pelo título abaixo.
        }
      }

      const result = await axios.get(
        `https://${language}.wikipedia.org/w/api.php`,
        {
          params: {
            action: 'query',
            prop: 'extracts|pageimages',
            exintro: true,
            explaintext: true,
            redirects: 1,
            piprop: 'original|thumbnail',
            pithumbsize: 1800,
            ...(pageId ? { pageids: pageId } : { titles: cityName }),
            format: 'json',
            origin: '*'
          }
        }
      )

      const pages = result.data?.query?.pages || {}
      return Object.values(pages)[0] as WikipediaPage | undefined
    }

    try {
      const portuguesePage = await fetchWikipediaPage('pt')
      const extract = portuguesePage?.extract?.trim()
      const image =
        portuguesePage?.original?.source || portuguesePage?.thumbnail?.source

      if (city.description) setCityDescription(city.description)
      else if (extract) setCityDescription(trimDescription(extract))

      if (!hasManagedCover) {
        if (image) setHeroImage(image)
        else {
          const englishPage = await fetchWikipediaPage('en')
          const englishImage =
            englishPage?.original?.source || englishPage?.thumbnail?.source
          if (englishImage) setHeroImage(englishImage)
        }
      }
    } catch {
      if (city.description) setCityDescription(city.description)
    }
  }, [city.description, id, latitude, longitude, name])

  const getExperienceEditorial = useCallback(async () => {
    try {
      const { data } = await api.get('/viator/experience-image', {
        params: {
          city: name,
          latitude,
          longitude
        }
      })

      if (data?.imageUrl) setExperienceImage(data.imageUrl)
    } catch {
      // Sem imagem da Viator, usamos uma imagem genérica no card.
    }
  }, [latitude, longitude, name])

  const getPartnerAds = useCallback(async () => {
    try {
      const { data } = await api.get(`/ads/city/${id}`)
      setPartnerAds(Array.isArray(data) ? data : [])
    } catch {
      setPartnerAds([])
    }
  }, [id])

  useEffect(() => {
    if (!isOpen) return

    setCityDetails(city)
    setCityDescription('')
    setHeroImage(undefined)
    setHeroCredit(null)
    setExperienceImage(undefined)
    setCurrencyInfo(null)
    setPartnerAds([])
    getCityDetails()
    getClimate()
    getVisitorContext()
    getItinerariesByCoordinate()
    getDestinationEditorial()
    getExperienceEditorial()
    getPartnerAds()
  }, [
    city,
    getCityDetails,
    getClimate,
    getDestinationEditorial,
    getExperienceEditorial,
    getItinerariesByCoordinate,
    getPartnerAds,
    getVisitorContext,
    isOpen
  ])

  useEffect(() => {
    if (!isOpen) return

    getCurrencyInfo(
      Number(latitude),
      Number(longitude),
      visitorContext.currency
    )
      .then(setCurrencyInfo)
      .catch(() => setCurrencyInfo(null))
  }, [isOpen, latitude, longitude, visitorContext.currency])

  useEffect(() => {
    if (!isOpen) return

    const timer = setInterval(() => setNow(new Date()), 30 * 1000)
    return () => clearInterval(timer)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !partnerAds.length) return

    partnerAds.forEach((ad) => {
      trackEvent({
        eventType: 'commercial_impression',
        entityType: 'partner_ad',
        entityId: ad.id,
        value: ad.partnerName,
        metadata: {
          cityId: id,
          city: name,
          format: ad.format
        }
      })
    })
  }, [id, isOpen, name, partnerAds])

  const isMobile = window.innerWidth < 800
  const slidesPerView = isMobile ? 1.25 : window.innerWidth < 1100 ? 2.2 : 3.2
  const visitorSymbol = currencySymbol(visitorContext.currency)
  const destinationSymbol =
    currencyInfo?.localSymbol ||
    (currencyInfo?.localCurrency
      ? currencySymbol(currencyInfo.localCurrency)
      : '')
  const destinationCurrencyLabel = currencyInfo?.localCurrency
    ? destinationSymbol &&
      normalizeLabel(destinationSymbol) !==
        normalizeLabel(currencyInfo.localCurrency)
      ? `${currencyInfo.localCurrency} ${destinationSymbol}`
      : currencyInfo.localCurrency
    : '--'
  const experienceCardImage = experienceImage || '/images/background_global.jpg'
  const viatorAffiliateUrl = getViatorAffiliateUrl(name)

  const horizontalPartnerAds = partnerAds
    .filter((ad) => ad.format === 'horizontal')
    .slice(0, 1)
  const cardPartnerAds = partnerAds
    .filter((ad) => ad.format === 'card')
    .slice(0, 2)

  const trackPartnerClick = (ad: PartnerAd) => {
    trackEvent({
      eventType: 'commercial_click',
      entityType: 'partner_ad',
      entityId: ad.id,
      value: ad.partnerName,
      metadata: {
        cityId: id,
        city: name,
        format: ad.format
      }
    })
  }

  const customStyles = {
    overlay: {
      zIndex: 1000,
      backgroundColor: 'rgba(20, 20, 20, 0.62)'
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: isMobile ? '100%' : 'min(1180px, 94vw)',
      height: isMobile ? '100%' : '92vh',
      padding: 0,
      border: 0,
      borderRadius: isMobile ? 0 : 18,
      overflow: 'auto'
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      style={customStyles}
    >
      <S.Wrapper>
        <S.BannerContainer>
          <S.BannerWrapper>
            <GoogleMapsImage
              name={name}
              latitude={latitude}
              longitude={longitude}
              fallbackImage={heroImage}
            >
              <S.RowContainer>
                <S.CloseButton onClick={closeModal} aria-label="Fechar ficha">
                  <img
                    src="/images/close_icon.png"
                    alt="fechar"
                    height={16}
                    width={16}
                  />
                </S.CloseButton>
              </S.RowContainer>

              <S.HeroContent>
                <S.Eyebrow>Ficha do destino</S.Eyebrow>
                <S.Title>{name}</S.Title>
              </S.HeroContent>

              {heroCredit?.provider === 'pexels' && heroCredit.sourceUrl && (
                <S.PhotoCredit
                  href={heroCredit.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Foto: {heroCredit.photographer || 'Pexels'} / Pexels
                </S.PhotoCredit>
              )}
            </GoogleMapsImage>
          </S.BannerWrapper>
        </S.BannerContainer>

        <S.FactsGrid>
          <S.FactCard>
            <S.FactTop>
              <S.FactIcon src="/images/temperature.svg" alt="clima" />
              <S.FactLabel>Clima agora</S.FactLabel>
            </S.FactTop>
            <S.FactValue>
              {temperature === null ? '--' : `${temperature}°C`}
            </S.FactValue>
            <S.FactSubvalue>
              {weatherLabel(weatherCode)}
              {apparentTemperature === null
                ? ''
                : ` · sensação ${apparentTemperature}°C`}
            </S.FactSubvalue>
          </S.FactCard>

          <S.FactCard>
            <S.FactTop>
              <S.FactEmoji aria-hidden="true">🕒</S.FactEmoji>
              <S.FactLabel>Hora local</S.FactLabel>
            </S.FactTop>
            <S.FactValue>{formatTime(now, destinationTimezone)}</S.FactValue>
            <S.FactSubvalue>
              No seu fuso: {formatTime(now, visitorContext.timezone)}
              {visitorContext.timezone ? ` · ${visitorContext.timezone}` : ''}
            </S.FactSubvalue>
          </S.FactCard>

          <S.FactCard>
            <S.FactTop>
              <S.FactIcon src="/images/currency.svg" alt="moeda" />
              <S.FactLabel>Moeda local</S.FactLabel>
            </S.FactTop>
            <S.FactValue>{destinationCurrencyLabel}</S.FactValue>
            <S.FactSubvalue>
              {currencyInfo
                ? currencyInfo.localCurrencyName || 'Moeda local indisponível'
                : 'Identificando moeda local'}
            </S.FactSubvalue>
          </S.FactCard>

          <S.FactCard>
            <S.FactTop>
              <S.FactIcon src="/images/currency.svg" alt="câmbio" />
              <S.FactLabel>Câmbio</S.FactLabel>
            </S.FactTop>
            <S.FactValue>
              {currencyInfo
                ? formatExchangeRate(
                    currencyInfo,
                    visitorSymbol,
                    destinationSymbol
                  )
                : '--'}
            </S.FactValue>
            <S.FactSubvalue>
              Referência {visitorContext.currency} ↔{' '}
              {currencyInfo?.localCurrency || '---'}
              {currencyInfo?.rateDate
                ? ` · referência ${currencyInfo.rateDate}`
                : ''}
            </S.FactSubvalue>
          </S.FactCard>
        </S.FactsGrid>

        <S.BodyContainer>
          <S.Section>
            <S.SectionHeader>
              <S.SectionTitle>Sobre {name.split(',')[0]}</S.SectionTitle>
            </S.SectionHeader>
            <S.Description>
              {cityDescription ||
                `Conheça ${name}, descubra seus principais atrativos e encontre roteiros da comunidade RedSterna que passam por este destino.`}
            </S.Description>
          </S.Section>

          <S.Section>
            <S.SectionHeader>
              <S.SectionTitle>
                Experiências em {name.split(',')[0]}
              </S.SectionTitle>
            </S.SectionHeader>
            <S.ExperienceCard>
              <S.ExperienceContent>
                <S.ExperienceText>
                  Veja passeios, atrações e atividades disponíveis para
                  complementar sua viagem.
                </S.ExperienceText>
                <S.ExperienceButton
                  type="button"
                  disabled={!viatorAffiliateUrl}
                  title={
                    viatorAffiliateUrl
                      ? `Explorar experiências em ${name.split(',')[0]}`
                      : 'Integração com experiências em breve para este destino'
                  }
                  onClick={() => {
                    if (!viatorAffiliateUrl) return
                    window.open(viatorAffiliateUrl, '_blank', 'noopener')
                  }}
                >
                  Explorar
                </S.ExperienceButton>
              </S.ExperienceContent>
              <S.ExperienceImage src={experienceCardImage} />
            </S.ExperienceCard>
          </S.Section>

          {partnerAds.length > 0 && (
            <S.Section>
              <S.SectionHeader>
                <S.SectionTitle>
                  Planeje sua viagem com parceiros locais
                </S.SectionTitle>
              </S.SectionHeader>

              {horizontalPartnerAds.map((ad) => (
                <S.PartnerWideLink
                  key={ad.id}
                  href={ad.destinationUrl}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  aria-label={`Visitar ${ad.partnerName}`}
                  onClick={() => trackPartnerClick(ad)}
                >
                  <picture>
                    {ad.mobileImageUrl && (
                      <source
                        media="(max-width: 620px)"
                        srcSet={ad.mobileImageUrl}
                      />
                    )}
                    <img src={ad.imageUrl} alt={ad.partnerName} />
                  </picture>
                </S.PartnerWideLink>
              ))}

              {cardPartnerAds.length > 0 && (
                <S.PartnerGrid>
                  {cardPartnerAds.map((ad) => (
                    <S.PartnerCardLink
                      key={ad.id}
                      href={ad.destinationUrl}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      aria-label={`Visitar ${ad.partnerName}`}
                      onClick={() => trackPartnerClick(ad)}
                    >
                      <picture>
                        {ad.mobileImageUrl && (
                          <source
                            media="(max-width: 620px)"
                            srcSet={ad.mobileImageUrl}
                          />
                        )}
                        <img src={ad.imageUrl} alt={ad.partnerName} />
                      </picture>
                    </S.PartnerCardLink>
                  ))}
                </S.PartnerGrid>
              )}
            </S.Section>
          )}

          <S.Section>
            <S.SectionHeader>
              <div>
                <S.SectionTitle>
                  Roteiros com {name.split(',')[0]}
                </S.SectionTitle>
                <S.SectionHint>
                  A ordem de relevância será refinada na próxima etapa.
                </S.SectionHint>
              </div>
            </S.SectionHeader>

            {itineraries && itineraries.length > 0 ? (
              <>
                <S.SwiperDiv
                  spaceBetween={16}
                  slidesPerView={slidesPerView}
                  navigation={itineraries.length > (isMobile ? 1 : 3)}
                  modules={[Navigation]}
                >
                  {itineraries.map(
                    ({
                      coverImage,
                      id: itineraryId,
                      title,
                      author,
                      days,
                      spent
                    }) => (
                      <S.Slide key={itineraryId}>
                        <S.RouteCard
                          to={getItineraryPublicPath({
                            id: itineraryId,
                            cities: [city]
                          })}
                        >
                          <S.RouteImage
                            src={coverImage || '/images/default-photo.png'}
                          />
                          <S.RouteCardContent>
                            <S.RouteTitle>{title}</S.RouteTitle>
                            <S.RouteMeta>
                              {days} dias
                              {Number(spent) > 0
                                ? ` · ${Number(spent).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                    maximumFractionDigits: 0
                                  })}`
                                : ' · Custo não informado'}
                            </S.RouteMeta>
                            {author?.firstname && (
                              <S.Author>
                                por {author.firstname} {author.lastname || ''}
                              </S.Author>
                            )}
                          </S.RouteCardContent>
                        </S.RouteCard>
                      </S.Slide>
                    )
                  )}
                </S.SwiperDiv>

                <S.SwiperFooter>
                  <S.RedirectButton
                    to={{
                      pathname: '/destinos',
                      search: `?city=${encodeURIComponent(name.split(',')[0])}`
                    }}
                  >
                    Ver todos os roteiros
                  </S.RedirectButton>
                </S.SwiperFooter>
              </>
            ) : (
              <S.EmptyState>
                Ainda não há roteiros públicos da RedSterna passando por este
                destino.
              </S.EmptyState>
            )}
          </S.Section>

          <S.AffiliateSection>
            <S.Tittle>
              <span>Planeje sua viagem com nossas </span>
              <span>dicas e parceiros</span>
            </S.Tittle>
            <S.ButtonsAffiliate>
              {getButtonAffiliateLinks.map((item) => (
                <S.AffiliateDiv key={item.title}>
                  <S.AffiliateLink
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                  >
                    <S.ButtonIcon src={item.image} alt={item.title} />
                  </S.AffiliateLink>
                  <S.AffiliateTitle>{item.title}</S.AffiliateTitle>
                </S.AffiliateDiv>
              ))}
            </S.ButtonsAffiliate>
          </S.AffiliateSection>
        </S.BodyContainer>
      </S.Wrapper>
    </Modal>
  )
}

export default CityModal
